import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { B } from '../constants';
import { destinations as destinationsAPI, partners as partnersAPI } from '../api';
import Footer from '../components/Footer';
import PartnerBadge from '../components/PartnerBadge';
import PromoStrip from '../components/PromoStrip';
import ReviewsSection from '../components/ReviewsSection';
import { FullPhoto } from '../components/FallbackImg';
import CityProse, { proseBlocks } from '../components/CityProse';
import PlaceSheet, { categoryLabel } from '../components/PlaceSheet';
import VerifiedPlacesSheet from '../components/VerifiedPlacesSheet';
import { bestMonthsLabel, conciseWhenToVisit } from '../lib/months';

// Parse a numeric city id from either a bare id or the "gb:city:<id>" form the API hands out.
function cityIdOf(d) {
  if (!d) return null;
  if (Number.isInteger(d.cityId) && d.cityId > 0) return d.cityId;
  const m = /^gb:city:(\d+)$/.exec(String(d.id ?? ''));
  const n = m ? Number(m[1]) : Number(d.id);
  return Number.isInteger(n) && n > 0 ? n : null;
}

// First paragraph of a CityContent markdown section, stripped of markdown syntax.
function firstParagraph(md, max = 220) {
  if (typeof md !== 'string') return null;
  const para = md.split(/\n\s*\n/).map((s) => s.trim()).find((s) => s && !/^#/.test(s));
  if (!para) return null;
  const text = para.replace(/[*_`>#]/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').replace(/\s+/g, ' ').trim();
  return text.length > max ? `${text.slice(0, max).replace(/\s+\S*$/, '')}…` : text;
}

const GUIDE_SECTIONS = [
  { key: 'whenToVisit',   title: 'When to visit',    icon: '📅' },
  { key: 'topThingsToDo', title: 'Top things to do', icon: '⭐' },
  { key: 'gettingThere',  title: 'Getting there',    icon: '✈️' },
  { key: 'gettingAround', title: 'Getting around',   icon: '🚇' },
  { key: 'whereToStay',   title: 'Where to stay',    icon: '🏨' },
  { key: 'foodAndDrink',  title: 'Food & drink',     icon: '🍽️' },
  { key: 'daytrips',      title: 'Day trips',        icon: '🧭' },
  { key: 'safetyTips',    title: 'Safety tips',      icon: '🛡️' },
  { key: 'cultureTips',   title: 'Culture tips',     icon: '🎭' },
];
const detailsStyle = { background: 'white', border: '1px solid #ece7de', borderRadius: 16, marginBottom: 10, padding: '16px 20px', boxShadow: '0 2px 10px rgba(15,25,35,0.04)' };
const summaryStyle = { cursor: 'pointer', fontWeight: 700, fontSize: 16, fontFamily: "'Montserrat',sans-serif", color: B.night };

const DAY_TRIPS_PREVIEW = 6;

function openGuideSection(key) {
  const el = document.getElementById(`guide-${key}`);
  if (!el) return;
  el.open = true;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// DayTrip rows often repeat a destination ("Versailles" and "Versailles Day Trip"):
// keep one per place, preferring the row that carries a travel time.
function dedupeDayTrips(rows) {
  const byKey = new Map();
  for (const d of rows || []) {
    const name = d.cityName || String(d.title || '').replace(/\s*day[\s-]*trip\s*$/i, '').trim() || d.title;
    const key = name.toLowerCase();
    const prev = byKey.get(key);
    if (!prev || (!prev.travelTime && d.travelTime)) byKey.set(key, { ...d, name });
  }
  return [...byKey.values()];
}

const SERIF = "'Playfair Display',serif";
const SANS = "'Montserrat',sans-serif";
const PAD = 'clamp(16px, 4vw, 40px)';
const kicker = { color: B.forest, fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', fontWeight: 700, fontFamily: SANS, margin: '0 0 6px' };
const h2Style = { fontFamily: SERIF, fontSize: 'clamp(24px, 4vw, 30px)', fontWeight: 700, margin: '0 0 18px', color: B.night };
const cardStyle = { background: 'white', border: '1px solid #ece7de', borderRadius: 20, boxShadow: '0 6px 24px rgba(15,25,35,0.06)' };
const chipStyle = { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.28)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', color: 'white', borderRadius: 999, padding: '7px 14px', fontSize: 13, fontWeight: 600, fontFamily: SANS };
const HIGHLIGHT_TINT = { '📍': '#e8f5ee', '🍽️': '#fdf1e2', '🏨': '#e9f1fb', '🚗': '#f1ecfa' };
const DETAIL_CSS = `
.gb-detail .sight { transition: transform .2s ease, box-shadow .2s ease; }
.gb-detail .sight:hover { transform: translateY(-3px); box-shadow: 0 12px 28px rgba(15,25,35,0.12); }
.gb-detail .sights { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 16px; }
@media (max-width: 600px) {
  .gb-detail .sights { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
  .gb-detail .sight-img { height: 110px !important; }
}
.gb-detail .gb-tap { transition: transform .2s ease, box-shadow .2s ease; }
.gb-detail .gb-tap:hover { transform: translateY(-2px); box-shadow: 0 12px 28px rgba(15,25,35,0.12); }
.gb-detail .gb-tap:focus-visible { outline: 2px solid ${B.forest}; outline-offset: 2px; }
.gb-detail .plan-btn { transition: transform .15s ease, opacity .15s ease; }
.gb-detail .plan-btn:not(:disabled):hover { transform: translateY(-1px); }
.gb-detail .plan-btn:disabled { opacity: .55; cursor: not-allowed; }
.gb-detail details > summary { list-style: none; display: flex; align-items: center; }
.gb-detail details > summary::-webkit-details-marker { display: none; }
.gb-detail details > summary::after { content: '+'; margin-left: auto; font-size: 20px; color: ${B.forest}; }
.gb-detail details[open] > summary::after { content: '–'; }
`;

export default function DetailScreen({ dest, goScreen }) {
  const { t } = useTranslation();
  const [fullDest, setFullDest] = useState(dest);
  const [overview, setOverview] = useState(null);
  const [content, setContent] = useState(null);
  const [introOpen, setIntroOpen] = useState(false);
  const [allTrips, setAllTrips] = useState(false);
  const [partners, setPartners] = useState([]);
  const [openPlace, setOpenPlace] = useState(null);
  const [placesOpen, setPlacesOpen] = useState(false);
  const cityId = cityIdOf(fullDest) ?? cityIdOf(dest);

  useEffect(() => {
    partnersAPI.list().then(res => setPartners(res.partners)).catch(() => {});
  }, []);

  useEffect(() => {
    destinationsAPI.get(dest.id)
      .then(r => setFullDest(r))
      .catch(() => setFullDest(dest));
  }, [dest]);

  useEffect(() => {
    if (!cityId) return undefined;
    let cancelled = false;
    setOverview(null);
    destinationsAPI.overview(cityId)
      .then((r) => { if (!cancelled) setOverview(r); })
      .catch(() => { if (!cancelled) setOverview(null); });
    return () => { cancelled = true; };
  }, [cityId]);

  useEffect(() => {
    if (!cityId) return undefined;
    let cancelled = false;
    setContent(null);
    setIntroOpen(false);
    destinationsAPI.getContent(cityId)
      .then((r) => { if (!cancelled) setContent(r?.content || null); })
      .catch(() => { if (!cancelled) setContent(null); });
    return () => { cancelled = true; };
  }, [cityId]);

  const [shortName, countryCode] = fullDest.name.split(',').map((x) => x.trim());
  // Wikipedia lead, shown with a credit, only for cities that have no generated guide yet.
  const aboutCredit = !content?.intro && !overview?.intro && !fullDest.description ? overview?.about : null;
  const introBlocks = proseBlocks(content?.intro || overview?.intro || fullDest.description || overview?.about?.text)
    .filter((b) => !(b.type === 'h' && fullDest.name.toLowerCase().startsWith(b.text.toLowerCase())));
  const leadBlock = introBlocks.find((b) => b.type === 'p');
  const introExpandable = introBlocks.length > 1 || (leadBlock && leadBlock.text.length > 320);
  const hasRating = Number.isFinite(fullDest.rating);
  const hasBudget = Number.isFinite(fullDest.budget);
  const bestMonthList = overview?.bestMonths || fullDest.bestMonths;
  const bestMonths = bestMonthsLabel(bestMonthList);
  const guideText = (key) => (key === 'whenToVisit' ? conciseWhenToVisit(content[key], bestMonthList) : content[key]);
  const topSights = overview?.topSights || [];
  const dayTrips = dedupeDayTrips(overview?.dayTrips);
  const shownTrips = allTrips ? dayTrips : dayTrips.slice(0, DAY_TRIPS_PREVIEW);
  const verifiedPlaces = overview?.verifiedPlaces || 0;
  // Guide sections the cards above already cover are left out.
  const guideSections = GUIDE_SECTIONS.filter((g) => content && typeof content[g.key] === 'string' && content[g.key].trim())
    .filter((g) => !(g.key === 'daytrips' && dayTrips.length))
    .filter((g) => !(g.key === 'topThingsToDo' && topSights.length));
  const faqs = Array.isArray(content?.faqsJson) ? content.faqsJson.filter((f) => f && f.q && f.a) : [];
  const facts = content
    ? [['🗣️', 'Language', content.language], ['💱', 'Currency', content.currency], ['🔌', 'Plugs', content.plugType], ['🕐', 'Time zone', content.timezone]].filter((f) => f[2])
    : [];

  // offerability only arrives with GET /api/destinations/:id; until then it's unknown.
  const offerabilityKnown = fullDest.offerability !== undefined;
  const aiPlannable = fullDest.offerability === 'OFFERABLE';

  const manualPlanCity = () => ({
    name: fullDest.name,
    cityId: fullDest.cityId ?? dest.cityId,
    placeId: fullDest.placeId,
    lat: fullDest.lat,
    lng: fullDest.lng,
  });
  const planManually = () => goScreen('manualPlanner', manualPlanCity());
  // "+ Add to trip" on a place: open Plan your trip for this city with the place on Day 1.
  const addPlaceToTrip = (p) => goScreen('manualPlanner', {
    ...manualPlanCity(),
    addPlace: { id: p.id, name: p.name, lat: p.lat, lng: p.lng, category: p.category, durationMin: p.durationMin, imageUrl: p.imageUrl },
  });
  const planWithAI = () => { if (aiPlannable) goScreen('ai', { prefillDestination: fullDest }); };

  // Highlights are built only from data we hold; a tile with nothing behind it is not shown.
  const highlights = [];
  if (verifiedPlaces > 0) {
    const top = overview.categories.filter((c) => !['restaurant', 'cafe', 'bar'].includes(c.category)).slice(0, 3);
    highlights.push({
      icon: '📍', title: 'Verified spots',
      desc: `${verifiedPlaces.toLocaleString()} verified places${top.length ? ` · ${top.map((c) => `${c.count} ${categoryLabel(c.category).toLowerCase()}`).join(', ')}` : ''}`,
      action: 'See all places on a map →',
      onClick: () => setPlacesOpen(true),
    });
  }
  if (overview && (overview.food.count > 0 || overview.food.medianMealUsd != null)) {
    const names = overview.food.places.slice(0, 3).map((p) => p.name).join(', ');
    const meal = overview.food.medianMealUsd != null ? `Typical meal ~$${overview.food.medianMealUsd}` : null;
    highlights.push({ icon: '🍽️', title: 'Local cuisine', desc: [names, meal].filter(Boolean).join(' · ') });
  }
  if (overview && overview.stay) {
    const tiers = [['Budget', overview.stay.budget], ['Comfort', overview.stay.comfort], ['Luxury', overview.stay.luxury]]
      .filter(([, v]) => v != null).map(([k, v]) => `${k} $${v}`);
    highlights.push({ icon: '🏨', title: 'Stay options', desc: `${tiers.join(' · ')} per night` });
  }
  const around = overview && firstParagraph(overview.gettingAround, 160);
  if (around) {
    const fullGuide = guideSections.some((g) => g.key === 'gettingAround');
    highlights.push({
      icon: '🚗', title: 'Getting around', desc: around,
      ...(fullGuide ? { action: 'Read more →', onClick: () => openGuideSection('gettingAround') } : {}),
    });
  }

  const section = { padding: `40px ${PAD} 0` };

  return (
    <div className="gb-detail">
      <style>{DETAIL_CSS}</style>
      <div style={{ padding: `20px ${PAD} 0` }}>
        <button onClick={() => goScreen('home')} aria-label="Back to Home" style={{ background: 'none', border: 'none', cursor: 'pointer', color: B.forest, fontSize: 14, fontWeight: 600, fontFamily: SANS, display: 'flex', alignItems: 'center', gap: 6 }}>
          ← Back to Home
        </button>
      </div>

      {/* Hero */}
      <div style={{ position: 'relative', height: 'clamp(340px, 52vw, 500px)', margin: `16px ${PAD} 0`, borderRadius: 28, overflow: 'hidden', background: B.night, boxShadow: '0 18px 40px rgba(15,25,35,0.18)' }}>
        {fullDest.img && <FullPhoto sources={[fullDest.img]} alt={fullDest.name} />}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(15,25,35,0.9) 0%, rgba(15,25,35,0.35) 50%, rgba(15,25,35,0.05) 100%)' }} />
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: 'clamp(22px, 4vw, 40px) clamp(22px, 4vw, 40px) 64px' }}>
          {countryCode && <div style={{ ...chipStyle, background: B.amber, border: 'none', color: B.night, fontWeight: 800, fontSize: 11, letterSpacing: 2, padding: '5px 12px', marginBottom: 12 }}>{countryCode}</div>}
          <h1 style={{ fontFamily: SERIF, fontSize: 'clamp(36px, 7vw, 64px)', lineHeight: 1.05, color: 'white', fontWeight: 700, margin: '0 0 16px' }}>{shortName}</h1>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {hasRating && <span style={chipStyle}>★ {fullDest.rating}{fullDest.ratingCount ? ` · ${fullDest.ratingCount} review${fullDest.ratingCount === 1 ? '' : 's'}` : ''}</span>}
            {hasBudget && <span style={chipStyle}>From ${Number(fullDest.budget).toLocaleString()}/day</span>}
            {bestMonths && <span style={chipStyle}>📅 {bestMonths}</span>}
            {verifiedPlaces > 0 && <span style={chipStyle}>📍 {verifiedPlaces.toLocaleString()} places</span>}
          </div>
        </div>
      </div>

      {/* About — short card, full intro on demand */}
      {leadBlock && (
        <div style={{ position: 'relative', zIndex: 1, margin: '-40px clamp(28px, 7vw, 80px) 0', maxWidth: 860 }}>
          <div style={{ ...cardStyle, padding: 'clamp(20px, 3vw, 30px)', borderTop: `4px solid ${B.amber}` }}>
            <p style={kicker}>About {shortName}</p>
            {introOpen
              ? <CityProse blocks={introBlocks} style={{ fontSize: 16, color: '#3b4450' }} />
              : <CityProse blocks={[leadBlock]} style={{ fontSize: 16, color: '#3b4450', margin: 0, display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }} />}
            {introExpandable && (
              <button type="button" onClick={() => setIntroOpen((o) => !o)} aria-expanded={introOpen}
                style={{ background: 'none', border: 'none', padding: 0, marginTop: 10, cursor: 'pointer', color: B.forest, fontWeight: 700, fontSize: 14, fontFamily: SANS }}>
                {introOpen ? 'Show less ↑' : 'Read more →'}
              </button>
            )}
            {aboutCredit?.url && (
              <p style={{ margin: '10px 0 0', fontSize: 12, color: '#8a8f98', fontFamily: SANS }}>
                Source:{' '}
                <a href={aboutCredit.url} target="_blank" rel="noopener noreferrer" style={{ color: '#8a8f98' }}>
                  {aboutCredit.source || 'Wikipedia'}
                </a>
              </p>
            )}
            {facts.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 18, paddingTop: 16, borderTop: '1px solid #f0ebe3' }}>
                {facts.map(([icon, label, value]) => (
                  <span key={label} title={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: B.sand, borderRadius: 999, padding: '6px 12px', fontSize: 13, color: B.night, fontFamily: SANS }}>
                    <span aria-hidden="true">{icon}</span>{value}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Highlights — real data only */}
      {highlights.length > 0 && (
        <div style={section}>
          <h2 style={h2Style}>Highlights</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
            {highlights.map(h => {
              const Tag = h.onClick ? 'button' : 'div';
              return (
                <Tag key={h.title} {...(h.onClick ? { type: 'button', onClick: h.onClick, className: 'gb-tap' } : {})}
                  style={{ ...cardStyle, borderRadius: 18, display: 'flex', gap: 14, alignItems: 'flex-start', padding: 18, textAlign: 'left', font: 'inherit', color: 'inherit', cursor: h.onClick ? 'pointer' : 'default' }}>
                  <div style={{ width: 44, height: 44, borderRadius: 14, background: HIGHLIGHT_TINT[h.icon] || '#eef5f1', display: 'grid', placeItems: 'center', fontSize: 20, flex: '0 0 auto' }}>{h.icon}</div>
                  <div>
                    <h4 style={{ fontSize: 15, fontWeight: 700, margin: '2px 0 4px', fontFamily: SANS }}>{h.title}</h4>
                    <p style={{ fontSize: 13, lineHeight: 1.55, color: B.muted, margin: 0, fontFamily: SANS }}>{h.desc}</p>
                    {h.action && <p style={{ fontSize: 13, fontWeight: 700, color: B.forest, margin: '6px 0 0', fontFamily: SANS }}>{h.action}</p>}
                  </div>
                </Tag>
              );
            })}
          </div>
        </div>
      )}

      {/* Top sights — the city's most notable verified places */}
      {topSights.length > 0 && (
        <div style={section}>
          <p style={kicker}>Verified places</p>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <h2 style={h2Style}>Top sights</h2>
            {verifiedPlaces > topSights.length && (
              <button type="button" onClick={() => setPlacesOpen(true)}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: B.forest, fontWeight: 700, fontSize: 14, fontFamily: SANS }}>
                See all {verifiedPlaces.toLocaleString()} places →
              </button>
            )}
          </div>
          <div className="sights">
            {topSights.map((p) => (
              <button key={p.id} type="button" className="sight gb-tap" onClick={() => setOpenPlace(p)} aria-label={`About ${p.name}`}
                style={{ ...cardStyle, borderRadius: 18, overflow: 'hidden', padding: 0, textAlign: 'left', font: 'inherit', color: 'inherit', cursor: 'pointer', display: 'block', width: '100%' }}>
                <div className="sight-img" style={{ position: 'relative', height: 140, overflow: 'hidden', background: 'linear-gradient(135deg, #e8f5ee, #f7f4ef)', display: 'grid', placeItems: 'center', fontSize: 30 }}>
                  {p.imageUrl
                    ? <FullPhoto sources={[p.imageUrl]} alt={p.name} />
                    : <span aria-hidden="true">📍</span>}
                </div>
                <div style={{ padding: '12px 14px 14px' }}>
                  <div style={{ fontFamily: SERIF, fontSize: 16, fontWeight: 700, lineHeight: 1.3 }}>{p.name}</div>
                  <div style={{ fontFamily: SANS, fontSize: 12, color: B.muted, marginTop: 4 }}>
                    {categoryLabel(p.category)}{p.durationMin ? ` · ⏱ ${p.durationMin} min visit` : ''}
                  </div>
                  <div style={{ fontFamily: SANS, fontSize: 12, fontWeight: 700, color: B.forest, marginTop: 6 }}>Why it’s popular →</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Day trips from this city */}
      {dayTrips.length > 0 && (
        <div style={section}>
          <p style={kicker}>Beyond the city</p>
          <h2 style={h2Style}>Day trips</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
            {shownTrips.map((d) => {
              const Tag = d.cityId ? 'button' : 'div';
              return (
              <Tag key={d.id}
                {...(d.cityId ? {
                  type: 'button', className: 'gb-tap', 'aria-label': `Explore ${d.name}`,
                  onClick: () => goScreen('detail', { id: `gb:city:${d.cityId}`, cityId: d.cityId, name: d.cityName || d.name, lat: d.lat, lng: d.lng }),
                } : {})}
                style={{ ...cardStyle, borderRadius: 16, display: 'flex', gap: 12, alignItems: 'center', padding: 14, textAlign: 'left', font: 'inherit', color: 'inherit', cursor: d.cityId ? 'pointer' : 'default' }}>
                <span style={{ width: 42, height: 42, borderRadius: 12, background: B.sand, display: 'grid', placeItems: 'center', fontSize: 22, flex: '0 0 auto' }} aria-hidden="true">{d.icon || '🧭'}</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, fontFamily: SANS }}>{d.name}</div>
                  {d.travelTime && <div style={{ fontSize: 12, color: B.muted, fontFamily: SANS }}>{d.travelTime} from {shortName}</div>}
                  {d.cityId && <div style={{ fontSize: 12, fontWeight: 700, color: B.forest, fontFamily: SANS, marginTop: 2 }}>What to do there →</div>}
                </div>
              </Tag>
              );
            })}
          </div>
          {dayTrips.length > DAY_TRIPS_PREVIEW && (
            <button type="button" onClick={() => setAllTrips((v) => !v)}
              style={{ background: 'none', border: 'none', padding: 0, marginTop: 14, cursor: 'pointer', color: B.forest, fontWeight: 700, fontSize: 14, fontFamily: SANS }}>
              {allTrips ? 'Show fewer ↑' : `Show all ${dayTrips.length} day trips →`}
            </button>
          )}
        </div>
      )}

      {/* Travel guide — CityContent sections */}
      {guideSections.length > 0 && (
        <div style={{ ...section, maxWidth: 900 }}>
          <p style={kicker}>Travel guide</p>
          <h2 style={h2Style}>Know before you go</h2>
          {guideSections.map((g) => (
            <details key={g.key} id={`guide-${g.key}`} style={detailsStyle}>
              <summary style={summaryStyle}><span aria-hidden="true" style={{ marginRight: 10 }}>{g.icon}</span>{g.title}</summary>
              <div style={{ marginTop: 12 }}><CityProse text={guideText(g.key)} /></div>
            </details>
          ))}
        </div>
      )}

      {faqs.length > 0 && (
        <div style={{ ...section, maxWidth: 900 }}>
          <h2 style={h2Style}>FAQs</h2>
          {faqs.map((f, i) => (
            <details key={i} style={detailsStyle}>
              <summary style={{ ...summaryStyle, fontSize: 15 }}>{f.q}</summary>
              <p style={{ marginTop: 10, marginBottom: 0, fontSize: 14, lineHeight: 1.7, color: B.muted, fontFamily: SANS }}>{f.a}</p>
            </details>
          ))}
        </div>
      )}

      {/* Plan — compact banner, after the reader has seen the city */}
      <div style={section}>
        <div style={{ borderRadius: 24, padding: 'clamp(24px, 4vw, 36px)', background: `linear-gradient(135deg, ${B.forest} 0%, #134a35 100%)`, color: 'white', display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 14px 34px rgba(26,107,74,0.25)' }}>
          <div style={{ flex: '1 1 280px' }}>
            <h2 style={{ fontFamily: SERIF, fontSize: 'clamp(22px, 3.5vw, 28px)', fontWeight: 700, margin: '0 0 6px', color: 'white' }}>Ready to plan your {shortName} trip?</h2>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'rgba(255,255,255,0.8)', fontFamily: SANS }}>
              {verifiedPlaces > 0
                ? `Build it day by day, or let us draft it from ${verifiedPlaces.toLocaleString()} verified places.`
                : 'Build it day by day, or let us draft it for you.'}
            </p>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, flex: '1 1 320px', justifyContent: 'flex-end' }}>
            <button type="button" className="plan-btn" onClick={planManually}
              style={{ flex: '1 1 150px', maxWidth: 240, background: 'transparent', color: 'white', border: '1.5px solid rgba(255,255,255,0.7)', borderRadius: 999, padding: '13px 20px', fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: SANS }}>
              <span aria-hidden="true">✏️ </span>{t('planner.manualTitle')}
            </button>
            <button type="button" className="plan-btn" onClick={planWithAI} disabled={!aiPlannable}
              style={{ flex: '1 1 150px', maxWidth: 260, background: B.amber, color: B.night, border: 'none', borderRadius: 999, padding: '13px 20px', fontWeight: 800, fontSize: 15, cursor: 'pointer', fontFamily: SANS }}>
              {offerabilityKnown && !aiPlannable
                ? t('planner.notPlannable')
                : <><span aria-hidden="true">✨ </span>{t('planner.aiTitle')}</>}
            </button>
          </div>
        </div>
      </div>

      {partners.length > 0 && (
        <div style={section}>
          <p style={{ ...kicker, marginBottom: 12 }}>Book with</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {partners.map(p => (
              <PartnerBadge key={p.slug} partner={p} destination={fullDest.name} compact />
            ))}
          </div>
        </div>
      )}

      <div style={{ height: 32 }} />
      {/* Reviews */}
      {cityId && <ReviewsSection destinationId={cityId} />}

      {/* Promo strip - videos from this destination */}
      <div style={{ padding: `0 ${PAD} 40px` }}>
        <PromoStrip destination={fullDest.name} label={`🎬 Stories from ${shortName}`} onPlanTrip={(p) => goScreen('ai', { prefillDestination: p.destination })} />
      </div>

      <Footer goScreen={goScreen} />

      {placesOpen && cityId && (
        <VerifiedPlacesSheet cityId={cityId} cityName={shortName} categoryLabel={categoryLabel}
          onOpenPlace={(p) => setOpenPlace(p)} onAddToTrip={addPlaceToTrip} onClose={() => setPlacesOpen(false)} />
      )}
      {openPlace && (
        <PlaceSheet place={openPlace} cityName={shortName} categoryLabel={categoryLabel} onAddToTrip={addPlaceToTrip}
          center={Number.isFinite(fullDest.lat) ? { lat: fullDest.lat, lng: fullDest.lng } : null}
          onClose={() => setOpenPlace(null)} />
      )}
    </div>
  );
}
